import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { usePagination } from '@abyss/web/hooks/usePagination';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { useToast } from '@abyss/web/hooks/useToast';
import { storage } from '@abyss/web/tools/storage';
import find from 'lodash/find';
import mapboxgl from 'mapbox-gl';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeSearchModifiedEvent } from '../../../../common/AdobeTagging/adobeSearchModifiedEvent';
import { adobeStandardSearchEvent } from '../../../../common/AdobeTagging/adobeStandardSearchEvent';
import { PSXHeaderCardWrapper } from '../../../../common/CardWrapper';
import {
  AdobeEventMethods,
  Constants,
  CoverageTypesCodes,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  GQL_API_NAMES,
  INITIAL_PAGE,
  PAGE_SIZE_MAP_DEFAULT,
  PAGE_SIZE_MAP_LARGE,
  ReverseCoverageTypesCodes,
  SHOULD_GET_HG_DATA,
} from '../../../../common/Constants';
import { FeatureFlags } from '../../../../common/ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import {
  mobileOnly,
  phoneOnly,
  viewFilterSortScreen,
} from '../../../../common/ConstantsStyles';
import { ContentWrapper } from '../../../../common/ContentWrapper';
import { EducateCompareModalPopup } from '../../../../common/EducateCompareModalPopup';
import { OutOfStateBanner } from '../../../../common/OutOfStateBanner';
import { PSXHeader } from '../../../../common/PSXHeader';
import { Breadcrumb } from '../../../../common/PSXHeader/Breadcrumb';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import {
  convertObjectToUrlParams,
  getFeatureFlag,
} from '../../../../common/Utils';
import {
  convertProviderTypeToAdobeType,
  getFormattedFilters,
  getSearchLocation,
} from '../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import {
  getDistanceFilterOption,
  getIsVirtualCareV2,
  getVirtualCareModel,
} from '../../../../common/Utils/filters.utils';
import {
  isMnrMember,
  isUmrMember,
} from '../../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../../context/PortalContext';
import { SearchFilterContext } from '../../../../context/SearchFilterContext';
import { ErrorBoundary } from '../../../../errors/ErrorBoundary';
import { uhcThemeOverride } from '../../../../frontends/themeOverrides/uhcThemeOverride';
import { useCompareProviders } from '../../../../hooks/useCompareProviders';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useGeoLocationStorage } from '../../../../hooks/useGeoLocationStorage';
import {
  useGetTokenOrUrlParams,
  useInitializeStore,
} from '../../../../hooks/useInitializeStore';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useParsedProviderFilters } from '../../../../hooks/useProviderFilterCount/useProviderFilterCount';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { useTier1PlanIdentifier } from '../../../../hooks/useTier1PlanIdentifier';
import { useViewAllData } from '../../../../hooks/useViewAllData';
import { BreadcrumbSessionStorage } from '../../../../models/BreadcrumbSessionStorage';
import { ProviderSearchFilters } from '../../../../models/ProviderSearch';
import { Directions } from '../../../../models/RouteDirections';
import { useAnalyticsStore } from '../../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../../store/useAnalyticsStore/analyticsStore';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../../store/useDetailsStore';
import { DetailsStore } from '../../../../store/useDetailsStore/detailsStore';
import { usePCPStore } from '../../../../store/usePCPStore';
import { PCPSearchStore } from '../../../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../../../store/useSearchStore';
import { SearchState } from '../../../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { handleMapClickLocation } from '../../../../utils/directions.utils';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import { getMapViewPageSize } from '../../../../utils/map.utils';
import { getMemberHealthCoverageType } from '../../../../utils/providerSearch.utils';
import { loginNotRequired } from '../../../../utils/session.utils';
import {
  getAdditionalAttributes,
  getClaimOrEligibilitySystemTypeCode,
  getCoverageEffDateRange,
  getCurrentMember,
  getMemberPlanYear,
  getNetworkIdsForPES,
  getPlanVariationCode,
  getRulesPackageKey,
} from '../../../../utils/user.utils';
import { useConfig } from '../../context/Analytics';
import { getLanguage } from '../../context/Internationalization/helpers';
import { CompareSmartChoice } from '../CompareSmartChoice/CompareSmartChoice';
import { MapView } from '../MapView';
import { AllPaginationProps } from '../MapView/AllPaginationProps';
import { CustomAttributesBlock } from '../MapView/CompareDrawer/Components/ConfirmationModal/ConfirmationModal';
import { EditCompareDrawerSmartChoice } from '../MapView/CompareDrawer/Components/EditCompare/EditCompareDrawerSmartChoice';
import { PickProviderModal } from '../MapView/CompareDrawer/Components/PickProviderModal';
import {
  CHECKBOX_ITEM_NAMES,
  CompareCheckboxInfo,
} from '../MapView/CompareDrawer/utility/compareDrawerConstants';
import { openTabOrNavigateOnClick } from '../MapView/CompareDrawer/utility/handleDetailsOnClick';
import { CompareDrawerProps } from '../MapView/CompareDrawerProps';
import { FiltersContainerDesktop } from '../MapView/FiltersContainerDesktop';
import { isGuestWithPlan } from '../MapView/utils';
import { VirtualCareList } from '../MapView/VirtualCareList';
import { getVirtualCareCode } from '../SearchResults/SearchResults.UtilsV2';
import { AlertsContent } from './AlertsContent';
import { MobileListView } from './MobileListView';
import PrioritySchedulingToast from './PrioritySchedulingToast';
import { ShowCompareFloatingSection } from './ShowCompareFloatingSection';
import { ShowShareFloatingSection } from './ShowShareFloatingSection';
import { successToast } from './Toast';
import { getDefaultCompareAttributes } from './utils/getDefaultCompareAttributes';
import { parseBreadcrumbs } from './utils/parseBreadcrumbs';
import { reSetMapAndListView } from './utils/reSetMapAndListView';
import {
  AlertBannerWrapper,
  FilterMapFullWidth,
  HeaderContainer,
  MapViewWrapper,
  MapViewWrapperFullWidth,
  PageContainer,
} from './ViewAll.style';
import { ViewAllDisclaimers } from './ViewAllDisclaimers';

const shouldSuppressVirtualCare = (
  isVirtualCareQuickCareSuppressed,
  coverageType,
  isVirtualCareUnetRailroadSuppressed
) =>
  isVirtualCareQuickCareSuppressed ||
  (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
    isVirtualCareUnetRailroadSuppressed);

export const ViewAll = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { searchFilters: selectedFilters, setSearchFilters } =
    useContext(SearchFilterContext);
  const { toast } = useToast();

  const urlParams = useGetTokenOrUrlParams();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setCoverageType: setStoreCoverageType } = chipStore;

  const virtualCareProviders = useVirtualCareStore(
    (state) => state.virtualCareProviders
  );

  const [filters, setFilters] =
    useState<ProviderSearchFilters>(selectedFilters);
  const [compareShareToggle, setCompareShareToggle] = useState<boolean>(false);
  const [isFiltersChanged, setIsFiltersChanged] = useState(false);
  const [isPageNav, setIsPageNav] = useState(false);
  const [navigateToDirections, setNavigateToDirections] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isEditCompareOpenSmartChoice, setIsEditCompareOpenSmartChoice] =
    useState<boolean>(false);
  const editCompareButtonRef = useRef<HTMLButtonElement>(null);
  const [isPickProviderModalOpen, setIsPickProviderModalOpen] =
    useState<boolean>(false);
  const [pickProviderIndex, setPickProviderIndex] = useState<number>(0);

  const [lastPageSize, setLastPageSize] = useState(0);
  const [isVirtualCareListView, setIsVirtualCareListView] = useState(() => {
    // Check URL for the parameter on initial load
    const params = new URLSearchParams(window.location.search);
    return params.get('virtualCareListViewPage') === 'true';
  });
  const map = React.useRef<mapboxgl.Map>(null);

  const [
    enableMapViewEnhancements,
    enableMapFilterSortCTA,
    enableTier1FromMemberRPK,
    costEnableFlag,
    eapCodeFlag,
    uspTierOneFlag,
    uspToggleFlag,
    showTier2Badge,
    enableMapViewLargeResults,
    showMergedCompareAndShare,
    enableBHVirtualCareFilterV2,
    enableMapViewHideOption,
    displayListViewFirst,
    showRaceAndEthnicity,
    showBccToast,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_FILTER_AND_SORT_CTA_MAP_VIEW,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER1_FROM_MEMBER_RPK,
    ConstantsLagoon.FEATURE_FLAGS.COST_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.EAP_CODE,
    ConstantsLagoon.FEATURE_FLAGS.USP_TIER1_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.USP_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_TWO_PROVIDER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_LARGE_RESULTS,
    ConstantsLagoon?.FEATURE_FLAGS?.ENABLE_COMPARE_SMART_CHOICE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_LIST_VIEW_FIRST,
    ConstantsLagoon.FEATURE_FLAGS.RACE_ETHNICITY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BCC_TOAST,
  ]);
  const mapViewPageSizeConfig = useConfig(
    ConstantsLagoon.CONFIG.MAP_VIEW_PAGE_SIZE
  );

  const mobileScreen = useMediaQuery(
    enableMapViewEnhancements ? mobileOnly : phoneOnly
  );

  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  useEffect(() => {
    const handleBeforePrint = () => setIsPrinting(true);
    const handleAfterPrint = () => setTimeout(() => setIsPrinting(false), 500);

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  const filterSortScreen = useMediaQuery(viewFilterSortScreen);
  const { navigate } = useRouter();

  const { chipValue, coverageType } = useInitializeStore(
    ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key
  );

  const { providerFilterList } = useParsedProviderFilters({
    selectedFilters,
  });

  const suppressPremiumCare: boolean = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );

  const suppressPatientReviews: boolean = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();

  const tierBenefits = useLagoon('tier-benefit')();
  const isBHCare: boolean =
    coverageType ===
    ReverseCoverageTypesCodes[CoverageTypesCodes.BEHAVIORAL_HEALTH];

  const isStandalone = loginNotRequired();
  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  const stickyHeaderDisabled = portalData?.portalConfig?.stickyHeaderDisabled;

  const urlCheck: boolean = !(isStandalone || isUmr);

  const shouldGetHGData = SHOULD_GET_HG_DATA;

  const isEapEligible = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.IS_BH_EAP_ELIGIBLE
  );

  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressVirtualCare = shouldSuppressVirtualCare(
    isVirtualCareQuickCareSuppressed,
    coverageType,
    isVirtualCareUnetRailroadSuppressed
  );
  const isSmartChoice = useSmartChoice();
  const defaultCompareAttribute = getDefaultCompareAttributes(
    coverageType,
    showMergedCompareAndShare && isSmartChoice
  );

  const currentMember = getCurrentMember();
  const { isCnSMember, isIFPEarlyPay, isIFPTieredPlan, lineOfBusiness } =
    currentMember || {};

  const showTierOneFlag = getFeatureFlag(
    featureFlags,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ].TIER_ONE
  );

  const showIFPTiers = isIFPTieredPlan && showTier2Badge;
  const finalCompareAttributeItems = defaultCompareAttribute
    .filter((item) => {
      if (item.name === CHECKBOX_ITEM_NAMES.PREMIUM_CARE_PROVIDER) {
        return !suppressPremiumCare;
      }
      if (item.name === CHECKBOX_ITEM_NAMES.REVIEWS) {
        return !suppressPatientReviews;
      }
      if (item.name === CHECKBOX_ITEM_NAMES.TIER_PROVIDER) {
        return showTierOneFlag && !(showIFPTiers || isCnSMember);
      }
      if (item.name === CHECKBOX_ITEM_NAMES.TIER) {
        return showTierOneFlag;
      }
      if (item.name === CHECKBOX_ITEM_NAMES.TIER_2_PROVIDER) {
        return showIFPTiers;
      }
      if (item.name === CHECKBOX_ITEM_NAMES.VIRTUAL_CARE_OFFERED) {
        return !suppressVirtualCare;
      }
      if (item.name === CHECKBOX_ITEM_NAMES.ETHNICITY) {
        return showRaceAndEthnicity;
      }
      if (item.name === CHECKBOX_ITEM_NAMES.PROVIDER_RACE) {
        return showRaceAndEthnicity;
      }
      return true;
    })
    .map((item) => {
      if (
        !costEnableFlag &&
        item.name === CHECKBOX_ITEM_NAMES.COST &&
        !showMergedCompareAndShare
      ) {
        return { ...item, isChecked: false };
      }
      return item;
    });
  const [items, setItems] = useState<CompareCheckboxInfo[]>(
    finalCompareAttributeItems
  );

  // set isDeeplinkAutoSearchRadiusQueryDone to false on mounting ViewAll so that
  // auto search radius query is triggered on load and also on page refresh.
  useEffect(() => {
    setSearchParams(
      (searchParams) => {
        if (searchParams.get('isDeeplinkAutoSearchRadiusQueryDone')) {
          searchParams.set('isDeeplinkAutoSearchRadiusQueryDone', 'false');
        }
        return searchParams;
      },
      { replace: true }
    );
  }, []);

  useEffect(() => {
    setItems(finalCompareAttributeItems);
  }, [JSON.stringify(finalCompareAttributeItems)]);

  const { toggleView } = useSearchStore(
    useShallow((state: SearchState) => ({
      toggleView: state.searchStore.toggleView,
    }))
  );

  const {
    search = '',
    searchTerm = '',
    searchMethod,
    providerType = '',
    heading = '',
    virtualCare = false,
  } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      searchTerm: state.typeaheadSearchStore.searchTerm,
      searchMethod: state.typeaheadSearchStore.searchMethod,
      providerType: state.typeaheadSearchStore.providerType,
      heading: state.typeaheadSearchStore.heading,
      virtualCare: state.typeaheadSearchStore.virtualCare,
    }))
  );

  const { choosePCP = false } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  const { linkName } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
    }))
  );

  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const isVisionCare = coverageType === ReverseCoverageTypesCodes.VISION;

  const [openMapview, setOpenMapview] = useState<boolean>(!mobileScreen);
  const [mobileRouteView, setMobileRouteView] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState(
    selectedFilters.PageNumber || INITIAL_PAGE
  );

  const [expandedProviderListView, setExpandedProviderListView] =
    useState<boolean>(() => {
      // checking toggleView from store first, if not fallback to URL params
      let toggleViewValue = toggleView;

      // If store doesn't have it, trying to read from URL directly
      if (!toggleViewValue) {
        const urlParams = new URLSearchParams(window.location.search);
        toggleViewValue = urlParams.get('toggleView') || undefined;
      }

      return reSetMapAndListView(
        toggleViewValue,
        enableMapViewHideOption,
        displayListViewFirst
      );
    });

  useEffect(() => {
    if (choosePCP) {
      setExpandedProviderListView(false);
    }
  }, [choosePCP]);

  const defaultPageSize = getMapViewPageSize(
    enableMapViewLargeResults,
    mapViewPageSizeConfig
  );
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const pageSizeOptions = enableMapViewLargeResults
    ? [defaultPageSize]
    : [PAGE_SIZE_MAP_DEFAULT, PAGE_SIZE_MAP_LARGE];
  const containerRef = useRef<HTMLDivElement>(null);

  const storedLocation = getGeoLocationFromStorage();
  const [breadcrumbUrls, setBreadcrumbUrls] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );
  const [previousLocation, setPreviousLocation] = useState(
    storedLocation?.name || ''
  );

  const {
    results,
    totalResultsCount,
    isLoading,
    isTieredPlan,
    headers,
    initialPage,
    sectionType,
    providerPinsResults,
    recommendationPreferences,
  } = useViewAllData({
    pageNumber,
    pageSize,
    coverageType,
    shouldGetHGData,
  });

  const getIsSuppressPremiumCarePhysician = () => {
    if ((recommendationPreferences as any)?.suppressPremium) {
      if ((recommendationPreferences as any)?.suppressPremium === 'Y') {
        return true;
      } else return false;
    }
    return false;
  };

  const suppressPremiumCarePhysician = getIsSuppressPremiumCarePhysician();

  const [providerPins, setProviderPins] = useState([]);

  useEffect(() => {
    if (providerPinsResults?.results) {
      setProviderPins(providerPinsResults.results);
    }
  }, [providerPinsResults]);

  const isVirtualCareV2 = getIsVirtualCareV2(
    enableBHVirtualCareFilterV2,
    coverageType,
    sectionType
  );
  const virtualCareModel = getVirtualCareModel(
    enableBHVirtualCareFilterV2,
    coverageType,
    sectionType
  );

  useEffect(() => {
    /** DE1094875: for couple of sec different screen shows before loading the page in responsive view from common provider searches.
     * Reason: This 'setIsMounted' is to ensure that the View is not rendered before the media query is evaluated. mobileScreen is false at first render and then true there after. So some nano secs we see Destokp view.
     * The useMediaQuery hook, which leverages the window.matchMedia() API, will return false at the first render if the window.matchMedia() API is not available.
     */
    setIsMounted(true);
    if (urlParams?.coverageType) {
      setStoreCoverageType(urlParams?.coverageType);
    }
    if (
      (urlParams?.virtualCare || virtualCare) &&
      !selectedFilters[virtualCareModel]
    ) {
      const distance =
        urlParams?.coverageType ===
        ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
          ? getDistanceFilterOption(0)
          : selectedFilters[FilterCollectionModel.DISTANCE];
      setSearchFilters({
        ...selectedFilters,
        [virtualCareModel]: virtualCareCode,
        [FilterCollectionModel.DISTANCE]: distance,
      });
    }
  }, []);

  const virtualCareCode = getVirtualCareCode(t, isVirtualCareV2);

  const isVirtualFilterCareSelected =
    (enableBHVirtualCareFilterV2 &&
      selectedFilters[virtualCareModel] &&
      Array.isArray(selectedFilters[virtualCareModel]) &&
      selectedFilters[virtualCareModel]?.[0]?.value ===
        virtualCareCode[0].value) ??
    false;

  // Handle browser navigation (back/forward buttons)
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      setIsVirtualCareListView(
        params.get('virtualCareListViewPage') === 'true'
      );
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [setIsVirtualCareListView]);

  useTier1PlanIdentifier(isTieredPlan);

  const totalPages = Math.ceil(totalResultsCount / pageSize);
  const { state, gotoPage, nextPage, previousPage, ...paginationProps } =
    usePagination({
      pages: totalPages,
      start: pageNumber,
    });

  const { currentPage } = state;
  const { firstPage } = paginationProps;

  useEffect(() => {
    if (!isEapEligible) {
      const newArray = items.filter((el) => el.name !== 'eapAccepted');
      setItems(newArray);
    }
  }, [isEapEligible, compareShareToggle]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      const totalPageCount = Math.ceil(totalResultsCount / lastPageSize);
      const prevPercentage = Math.ceil((pageNumber / totalPageCount) * 100);
      const updatedPageNumber = Math.ceil((prevPercentage / 100) * totalPages);
      // abbys pagination starts with 0 that's why -1 is required
      gotoPage(updatedPageNumber - 1);
    }
  }, [pageSize]);

  useEffect(() => {
    if (pageNumber !== currentPage) {
      setIsPageNav(true);
      setPageNumber(currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    if (isPrinting) {
      return;
    }
    setOpenMapview(!mobileScreen);
    if (mobileScreen) {
      setExpandedProviderListView(false);
    }
  }, [mobileScreen]);

  const serializedSelectedFilters = getFormattedFilters(selectedFilters);
  useEffect(() => {
    if (!isLoading && totalResultsCount === 0) {
      // TODO - add url params
      /** We don't have to replace as from null result page we are pushing back to this.
       * we are navigating to a new URL and hence we don't need to replace.
       */
      const searchParamsObject = Object.fromEntries(searchParams.entries());
      const urlParams = convertObjectToUrlParams(
        {} as ChipState,
        searchParamsObject
      );
      navigate(`${ConstantsRoutes.NULL_SPECIALTY_RESULTS.path}/${urlParams}`);
    } else if (totalResultsCount !== undefined) {
      // guided should not have search term
      const searchMethodForAnalytics = searchMethod ?? AdobeEventMethods.Guided;
      const searchTermForAnalytics =
        searchMethodForAnalytics === AdobeEventMethods.Guided ? '' : searchTerm;
      const isFromMixedResults = !!searchTerm;
      const analyticsBody = {
        linkName,
        filters: serializedSelectedFilters,
        numberOfResults: totalResultsCount,
        method: searchMethod ?? AdobeEventMethods.Guided,
        customAttributesBlock: {
          providerType: convertProviderTypeToAdobeType(providerType),
          searchLocation: getSearchLocation(storedLocation),
        },
        term: searchTermForAnalytics,
      };
      if (!isPageNav) {
        // if we are changing pages, there's no change in search really,
        // so we don't want to post standard search or search modified.
        if (isFiltersChanged) {
          if (!isLoading) {
            adobeSearchModifiedEvent(analyticsBody);
          }
        } else {
          !isFromMixedResults &&
            totalResultsCount > 0 &&
            adobeStandardSearchEvent(analyticsBody);
        }
      } else {
        setIsPageNav(false);
      }
    }
  }, [results, isLoading]);

  useEffect(() => {
    if (JSON.stringify(selectedFilters) !== JSON.stringify(filters)) {
      firstPage();
      setFilters(selectedFilters);
      setIsFiltersChanged(true);
      setPageNumber(INITIAL_PAGE);
    }
  }, [JSON.stringify(selectedFilters)]);

  useEffect(() => {
    if (initialPage) {
      setPageSize(defaultPageSize);
    }
  }, [initialPage]);

  const breadcrumbs = parseBreadcrumbs(breadcrumbUrls, sectionType, t);

  useEffect(() => {
    breadcrumbUrls[ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.key] =
      ConstantsRoutes.PROVIDER_SEARCH_RESULTS_MAP_VIEW.path +
      window.location.search;
    setBreadcrumbUrls(breadcrumbUrls);
  }, [window.location.search]);

  const { IS_BH_EAP_ELIGIBLE } = Constants.STORAGE_KEYS.SESSION;
  urlParams?.eapAccepted &&
    storage.session.set(IS_BH_EAP_ELIGIBLE, urlParams?.eapAccepted);

  useEffect(() => {
    document.title = `${t(sectionType)} ${t('results for')} ${
      t(`${search}`) || ''
    } | ${Constants.SITE_NAME}`;

    if (urlParams?.eapAccepted) {
      const eapOption = {
        label: t(EAP_OPTION_LABEL),
        dnt_label: t(EAP_OPTION_LABEL),
        value: 'A045,E475',
      };
      selectedFilters.AreaOfExpertise = [
        ...(selectedFilters?.AreaOfExpertise || []),
        eapOption,
      ];
    }
  }, []);

  const selectedSearchRadius: any = selectedFilters?.Distance?.find(
    (item) => item.value
  )?.value;

  const [selectedCheckbox, setSelectedCheckbox] = useState({ checked: {} });
  const [selectedItems, setSelectedItems] = useState<any>([]);
  const [openCompare, setOpenCompare] = useState<boolean>(false);
  const [openShare, setOpenShare] = useState<boolean>(false);
  const [openBoxContents, setOpenBoxContents] = useState<boolean>(
    !!selectedItems.length
  );

  const [isShareAllResults, setIsShareAllResults] = useState<boolean>(false);
  const [isCompareClicked, setIsCompareClicked] = useState<boolean>(false);
  const toastMessage = t('Your results have been shared');
  const [selectedId, setSelectedId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );
  const [routeEndCoords, setRouteEndCoords] = useState<
    [number | null, number | null]
  >([null, null]);

  const [directions, setDirections] = useState<Directions>({
    userPlaceName: '',
    endPlaceName: '',
    routes: [],
  });

  const { longitude, latitude } = getGeoLocationFromStorage();
  const isOpenCompareShare = openCompare || openShare;

  const closeAllDrawersAndOpenSuccessAlert = () => {
    setOpenCompare(false);
    setOpenShare(false);
    // after integration, check for success here
    successToast(toastMessage);
  };

  /** BCC TOAST */
  const [searchParams] = useSearchParams();
  const isEnableBCCSearch = searchParams.get('enableBccSearch');

  const allProvidersObj =
    find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING.PRIORITY_SCHEDULING_ALL_PROVIDERS,
    }) || {};

  const prioritySchedulingObj =
    find(uiMessaging, {
      key: ConstantsLagoon.UI_MESSAGING.PRIORITY_SCHEDULING_BCC_PROVIDER,
    }) || {};

  const prevBccFilterRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (isEnableBCCSearch && showBccToast) {
      const currentBccValue =
        selectedFilters[FilterCollectionModel.PRIORITY_SCHEDULING]?.value ===
        true;

      if (prevBccFilterRef.current === undefined) {
        prevBccFilterRef.current = currentBccValue;
        return;
      }

      if (prevBccFilterRef.current === currentBccValue) {
        return;
      }

      prevBccFilterRef.current = currentBccValue;

      const {
        id: prioritySchedulingId = '',
        message: prioritySchedulingMessage = '',
        title: prioritySchedulingTitle = '',
        icon: prioritySchedulingIcon = '',
      } = prioritySchedulingObj;

      const {
        id: allProvidersId = '',
        message: allProvidersMessage = '',
        title: allProvidersTitle = '',
        icon: allProvidersIcon = '',
      } = allProvidersObj;

      // Clear both session flags before showing new toast
      // This ensures toast can show consistently when filter is toggled multiple times
      storage.session.remove(prioritySchedulingId);
      storage.session.remove(allProvidersId);

      // When BCC is deselected, also hide the priority scheduling toast
      // so allProviders toast can be shown immediately
      if (!currentBccValue) {
        toast.hide(prioritySchedulingId);
      }

      const prioritySchedulingProviderUndoObj =
        find(uiMessaging, {
          key: ConstantsLagoon.UI_MESSAGING
            .PRIORITY_SCHEDULING_BCC_PROVIDER_UNDO,
        }) || {};

      const { message: prioritySchedulingProviderButtonContent = '' } =
        prioritySchedulingProviderUndoObj;

      const isPrioritySchedulingFilterSelected = currentBccValue;

      PrioritySchedulingToast({
        setSearchFilters,
        selectedFilters,
        message: isPrioritySchedulingFilterSelected
          ? prioritySchedulingMessage
          : allProvidersMessage,
        title: isPrioritySchedulingFilterSelected
          ? prioritySchedulingTitle
          : allProvidersTitle,
        icon: isPrioritySchedulingFilterSelected
          ? prioritySchedulingIcon
          : allProvidersIcon,
        isPriorityScheduleSelected: isPrioritySchedulingFilterSelected,
        toastId: isPrioritySchedulingFilterSelected
          ? prioritySchedulingId
          : allProvidersId,
        ids: {
          prioritySchedulingId,
          allProvidersId,
        },
        buttonLabel: prioritySchedulingProviderButtonContent,
        providerFilters: providerFilterList,
      });
    }
  }, [
    selectedFilters[FilterCollectionModel.PRIORITY_SCHEDULING]?.value,
    isEnableBCCSearch,
    showBccToast,
    uiMessaging,
  ]);

  useEffect(
    () => () => {
      toast.hide(allProvidersObj?.id);
      toast.hide(prioritySchedulingObj?.id);
    },
    [allProvidersObj?.id, prioritySchedulingObj?.id]
  );

  // begin: a11y keyboard navigation
  const { compareProvidersFlow } = useStore(StoreKeys.UI_STATE);
  const { indexOflastProviderSelected, shouldFocusLastIndex } =
    compareProvidersFlow;

  useEffect(() => {
    const cards = document.querySelectorAll("input[type='checkbox']");
    if (cards && shouldFocusLastIndex) {
      const lastSelectedCard = cards[
        indexOflastProviderSelected
      ] as HTMLInputElement;
      if (lastSelectedCard) lastSelectedCard?.focus();
    }
  }, [openCompare, shouldFocusLastIndex]);

  const { shareProvidersFlow } = useStore(StoreKeys.UI_STATE);
  const {
    indexOflastProviderSelected: indexOflastShareProviderSelected,
    shouldFocusLastIndex: shouldFocusLastShareProviderIndex,
  } = shareProvidersFlow;

  useEffect(() => {
    const cards = document.querySelectorAll("input[type='checkbox']");
    if (cards && shouldFocusLastShareProviderIndex) {
      const lastSelectedCard = cards[
        indexOflastShareProviderSelected
      ] as HTMLInputElement;
      if (lastSelectedCard) lastSelectedCard?.focus();
    }
  }, [openShare, shouldFocusLastShareProviderIndex]);
  // end: a11y keyboard navigation

  const [, getCompareDetails] = useCompareProviders({
    onCompleted: (result) => result,
    onError: () => {},
  });

  const memCatergory = currentMember?.membershipCategory || '';
  const membershipCategory = uspToggleFlag ? memCatergory : null;

  const { population } = currentMember;
  const isGuestUser = portalData?.portalConfig?.isGuestUser;
  const { stateCode } = useGeoLocationStorage();
  const isMNR = isMnrMember(lineOfBusiness);
  const isPreEffective = useCurrentMemberIsPreEffective();
  const currentMemberEffectiveDate = getCoverageEffDateRange(
    currentMember,
    coverageType
  ).startDate;
  const additionalAttributes = useLagoon('additional-attributes')();
  const addSelectedProvider = async (providerData) => {
    const index = selectedItems.findIndex(
      (provider) => provider.locationId === providerData.locationId
    );
    if (index === -1) {
      showMergedCompareAndShare &&
        setSelectedItems((prevState) => [...(prevState || []), providerData]);
      const locale = getLanguage()?.code || 'en';
      const medicalCoverage = getMemberHealthCoverageType(
        currentMember?.eligibility,
        CoverageTypesCodes.MEDICAL
      );
      const result = await getCompareDetails({
        variables: {
          lob: lineOfBusiness,
          locale,
          additionalAttributes: getAdditionalAttributes(
            additionalAttributes,
            currentMember,
            coverageType,
            providerType,
            GQL_API_NAMES.COMPARE
          ),
          membershipCategory,
          providerId: providerData.providerId,
          providerType: providerData.providerType,
          latitude: providerData.latitude || '',
          longitude: providerData.longitude || '',
          stateCode,
          locationId: providerData.locationId || '',
          reciprocityId: getNetworkIdsForPES(
            currentMember,
            coverageType,
            featureFlags
          ),
          snpType:
            coverageType == ReverseCoverageTypesCodes.MEDICAL
              ? medicalCoverage?.snpType
              : '',
          rulesPackageKey: getRulesPackageKey(
            currentMember,
            tierBenefits,
            providerType,
            uspTierOneFlag,
            enableTier1FromMemberRPK
          ),
          planVariationCode: getPlanVariationCode(currentMember, coverageType),
          population,
          claimSystemTypeCode: getClaimOrEligibilitySystemTypeCode(
            currentMember,
            coverageType
          )?.claimSystemTypeCode,
          eligibilitySystemTypeCode: getClaimOrEligibilitySystemTypeCode(
            currentMember,
            coverageType
          )?.eligibilitySystemTypeCode,
          planYear: getMemberPlanYear(
            isPreEffective,
            currentMemberEffectiveDate
          ),
          memberEffectiveDate: isPreEffective
            ? currentMemberEffectiveDate
            : null,
          policyId: currentMember?.policyNumber,
          coverageType,
          isIFPEarlyPay,
          isGuestUser,
          cesProductCode: medicalCoverage?.productCode,
          memberMarketType: medicalCoverage?.marketType,
        },
      });

      const compareProviderData = result.data.compare.provider;
      let finalData = providerData;
      if (compareProviderData) {
        finalData = { ...providerData, ...compareProviderData };
      }
      if (showMergedCompareAndShare) {
        setSelectedItems((prevState) =>
          (prevState || []).map((item) =>
            item.locationId === finalData.locationId ? finalData : item
          )
        );
      } else setSelectedItems((prevState) => [...prevState, finalData]);
    } else {
      const selected = selectedItems;
      selected.splice(index, 1);
      setSelectedItems([...selected]);
    }
  };

  const removeItem = (locationId: string) => {
    requestAnimationFrame(() => {
      setSelectedCheckbox((prevState) => ({
        checked: {
          ...prevState.checked,
          [locationId]: !prevState.checked[locationId],
        },
      }));
      const selectedProvider = selectedItems.find(
        (provider) => provider.locationId === locationId
      );
      addSelectedProvider(selectedProvider); // To remove selected provider from compare drawer
      if (selectedItems?.length < 2) {
        setIsCompareClicked(false);
      }
    });
  };
  const showAlerts = !(mobileScreen && openMapview);
  const url = useMemo(() => window.location.href, []);
  const guestPlan = useMemo(() => isGuestWithPlan(url), [url]);

  const handleDetailsOnClickCb = (
    providerId,
    options,
    customAttributesBlock: CustomAttributesBlock
  ) => {
    openTabOrNavigateOnClick({
      providerId,
      navigate,
      options,
      chipStoreValues: { chipValue, coverageType },
      setDetailsStore,
      customAttributesBlock,
      isIFPEarlyPay,
      isGuestUser,
      isGuestWithPlan: guestPlan,
    });
  };

  const compareDrawerProps: CompareDrawerProps = {
    map,
    openCompare,
    setOpenCompare,
    selectedItems,
  };

  const allPaginationProps: AllPaginationProps = {
    pageSize,
    pageSizeOptions,
    paginationProps,
    setPageSize,
    setLastPageSize,
    gotoPage,
    nextPage,
    previousPage,
    pageNumber,
  };

  const mapOnPremKey = useConfig('MAPBOX_ONPREM_KEY');
  const mapKey = useConfig('MAPBOX_KEY');
  const mapOnPremURL = useConfig('MAPBOX_ONPREM_URL');

  const handleClickLocation = async (locationId, locationLng, locationLat) => {
    await handleMapClickLocation(
      map.current,
      selectedId,
      longitude,
      latitude,
      locationId,
      locationLng,
      locationLat,
      mapOnPremKey,
      mapKey,
      mapOnPremURL,
      portalData?.portalConfig?.mapLocationUrl,
      mobileScreen,
      setDirections,
      setRouteEndCoords,
      setSelectedId
    );
  };

  const onSwapProviderPress = (value) => {
    if (Number(value) !== 0) {
      const updatedItems = [...selectedItems];
      const temp = updatedItems[Number(pickProviderIndex)];
      updatedItems[Number(pickProviderIndex)] = updatedItems[Number(value) + 1];
      updatedItems[Number(value) + 1] = temp;
      setSelectedItems(updatedItems);
    }
  };
  const MapViewContent = () => (
    <>
      <MapView
        addSelectedProvider={addSelectedProvider}
        allPaginationProps={allPaginationProps}
        chipValue={chipValue}
        choosePCP={choosePCP}
        compareDrawerProps={compareDrawerProps}
        compareShareToggle={compareShareToggle}
        containerRef={containerRef}
        coverageType={coverageType}
        directions={directions}
        enableMapFilterSortCTA={enableMapFilterSortCTA}
        enableMapViewEnhancements={enableMapViewEnhancements}
        expandedProviderListView={expandedProviderListView}
        filterProviderResultsPrintViewProps={{
          initialPage,
          coverageType,
          filters,
        }}
        headers={headers}
        heading={heading}
        isLoading={isLoading}
        isMNR={isMNR}
        isPageNav={isPageNav}
        isTieredPlan={isTieredPlan}
        items={items}
        mobileRouteView={mobileRouteView}
        navigateToDirections={navigateToDirections}
        openMapview={openMapview}
        openShare={openShare}
        previousLocation={previousLocation}
        providerPins={providerPins}
        providerType={providerType}
        recommendationPreferences={recommendationPreferences}
        removeItem={removeItem}
        results={results}
        routeEndCoords={routeEndCoords}
        search={search}
        searchMethod={searchMethod}
        searchRadius={selectedSearchRadius}
        searchTerm={searchTerm}
        sectionType={sectionType}
        selectedCheckbox={selectedCheckbox}
        selectedFilters={serializedSelectedFilters}
        setCompareShareToggle={setCompareShareToggle}
        setDirections={setDirections}
        setExpandedProviderListView={setExpandedProviderListView}
        setIsCompareClicked={() => setIsCompareClicked(true)}
        setIsVirtualCareListView={setIsVirtualCareListView}
        setItems={setItems}
        setMobileRouteView={setMobileRouteView}
        setNavigateToDirections={setNavigateToDirections}
        setOpenBoxContents={setOpenBoxContents}
        setOpenMapview={setOpenMapview}
        setOpenShare={setOpenShare}
        setPreviousLocation={setPreviousLocation}
        setRouteEndCoords={setRouteEndCoords}
        setSelectedCheckbox={setSelectedCheckbox}
        setSelectedItems={setSelectedItems}
        showMergedCompareAndShare={showMergedCompareAndShare}
        totalResultsCount={totalResultsCount}
      />
      <ShowShareFloatingSection
        closeAllDrawersAndOpenSuccessAlert={closeAllDrawersAndOpenSuccessAlert}
        handleDetailsOnClickCb={handleDetailsOnClickCb}
        isShareAllResults={isShareAllResults}
        items={items}
        map={map}
        mobileScreen={mobileScreen}
        openBoxContents={openBoxContents}
        openShare={openShare}
        removeItem={removeItem}
        selectedItems={selectedItems}
        setIsShareAllResults={setIsShareAllResults}
        setOpenBoxContents={setOpenBoxContents}
        setOpenShare={setOpenShare}
        setSelectedCheckbox={setSelectedCheckbox}
        setSelectedItems={setSelectedItems}
      />
    </>
  );

  const MobileMapViewContent = () => (
    <MobileListView
      addSelectedProvider={addSelectedProvider}
      allPaginationProps={allPaginationProps}
      chipValue={chipValue}
      choosePCP={choosePCP}
      compareShareToggle={compareShareToggle}
      enableMapFilterSortCTA={enableMapFilterSortCTA}
      enableMapViewEnhancements={enableMapViewEnhancements}
      enableMapViewHideOption={enableMapViewHideOption}
      expandedProviderListView={expandedProviderListView}
      filterProviderResultsPrintViewProps={{
        initialPage,
        coverageType,
        filters,
      }}
      gotoPage={gotoPage}
      headers={headers}
      heading={heading}
      isLoading={isLoading}
      isMNR={isMNR}
      map={map}
      mobileRouteView={mobileRouteView}
      navigateToDirections={navigateToDirections}
      nextPage={nextPage}
      openCompare={openCompare}
      openMapview={openMapview}
      openShare={openShare}
      pageSize={pageSize}
      paginationProps={paginationProps}
      previousPage={previousPage}
      recommendationPreferences={recommendationPreferences}
      results={results}
      search={search}
      searchTerm={searchTerm}
      sectionType={sectionType}
      selectLocation={handleClickLocation}
      selectedCheckbox={selectedCheckbox}
      selectedFilters={serializedSelectedFilters}
      setCompareShareToggle={setCompareShareToggle}
      setExpandedProviderListView={setExpandedProviderListView}
      setIsCompareClicked={() => setIsCompareClicked(true)}
      setNavigateToDirections={setNavigateToDirections}
      setOpenBoxContents={setOpenBoxContents}
      setOpenCompare={setOpenCompare}
      setOpenMapview={setOpenMapview}
      setOpenShare={setOpenShare}
      setSelectedCheckbox={setSelectedCheckbox}
      setSelectedItems={setSelectedItems}
      setVirtualCareListView={setIsVirtualCareListView}
      totalResultsCount={totalResultsCount}
    />
  );
  const HeaderViewContent = () => (
    <ErrorBoundary>
      <HeaderContainer
        cssProps={{
          urlCheck,
          enableMapViewEnhancements,
          enableMapFilterSortCTA,
        }}
        data-auto-testid="header-container"
        data-testid="header-container"
      >
        <PSXHeader
          breadcrumbs={breadcrumbs}
          dataTestId="view-all-on-submit"
          showChips={false}
          showChoosePCPHeader={choosePCP}
          showMemberSelection={choosePCP ? false : !mobileScreen}
          showSearchInputBackButton={choosePCP ? false : mobileScreen}
        />
      </HeaderContainer>
    </ErrorBoundary>
  );

  const getHeaderContent = (
    enableMapViewHideOption,
    expandedProviderListView,
    navigateToDirections,
    HeaderViewContent
  ) => {
    if (enableMapViewHideOption) {
      if (expandedProviderListView && navigateToDirections) {
        return null;
      }
      return HeaderViewContent();
    }
    return HeaderViewContent();
  };
  const headerContent = getHeaderContent(
    enableMapViewHideOption,
    expandedProviderListView,
    navigateToDirections,
    HeaderViewContent
  );
  const FilterViewContent = () => (
    <FiltersContainerDesktop
      choosePCP={choosePCP}
      enableMapFilterSortCTA={enableMapFilterSortCTA}
      expandedProviderListView={expandedProviderListView}
      filterSortScreen={filterSortScreen}
      previousLocation={previousLocation}
      resultSet={results}
      sectionType={sectionType}
      setExpandedProviderListView={setExpandedProviderListView}
      setNavigateToDirections={setNavigateToDirections}
      setPreviousLocation={setPreviousLocation}
      suppressPremiumCarePhysician={suppressPremiumCarePhysician}
    />
  );
  const filterViewContent =
    enableMapViewHideOption && !mobileScreen ? (
      expandedProviderListView && !choosePCP ? (
        !navigateToDirections && (
          <div
            style={{
              borderBottom: '1px solid rgb(229, 229, 230)',
            }}
          >
            <MapViewWrapper>{FilterViewContent()}</MapViewWrapper>
          </div>
        )
      ) : (
        <FilterMapFullWidth
          cssProps={{
            urlCheck,
            stickyHeaderDisabled,
          }}
        >
          {FilterViewContent()}
        </FilterMapFullWidth>
      )
    ) : (
      enableMapFilterSortCTA &&
      !mobileScreen &&
      !compareShareToggle && (
        <FilterMapFullWidth
          cssProps={{
            urlCheck,
            stickyHeaderDisabled,
          }}
        >
          {FilterViewContent()}
        </FilterMapFullWidth>
      )
    );

  const virtualCareContent = (
    <>
      <VirtualCareList
        heading={heading}
        isLoading={isLoading}
        isMNR={isMNR}
        mobileScreen={mobileScreen}
        searchTerm={searchTerm}
        sectionType={sectionType}
        serviceType={virtualCareProviders?.[0]?.serviceType}
        setIsVirtualCareListView={setIsVirtualCareListView}
        virtualCareProviders={virtualCareProviders}
      />
    </>
  );

  const mapViewContent = enableMapViewEnhancements ? (
    enableMapViewHideOption &&
    expandedProviderListView &&
    !navigateToDirections ? (
      <MapViewWrapper css={{ paddingTop: '0px !important' }}>
        {MapViewContent()}
      </MapViewWrapper>
    ) : (
      <MapViewWrapperFullWidth
        cssProps={{
          expandedProviderListView,
          enableMapViewHideOption,
          navigateToDirections,
          isOpenCompareShare,
          enableMapViewEnhancements,
        }}
        data-testid="map-view-wrapper-full-width"
      >
        {MapViewContent()}
      </MapViewWrapperFullWidth>
    )
  ) : (
    <MapViewWrapper>{MapViewContent()}</MapViewWrapper>
  );

  const mobileMapViewContent =
    mobileScreen &&
    (enableMapViewEnhancements ? (
      MobileMapViewContent()
    ) : (
      <ContentWrapper>{MobileMapViewContent()}</ContentWrapper>
    ));
  const isProviderSection = sectionType === Constants.RESULT_SECTION.PROVIDER;
  const showCompareExpandedView =
    isCompareClicked && showMergedCompareAndShare && isProviderSection;

  if (!isMounted) return null;

  return (
    <div
      ref={containerRef}
      style={{ overflow: 'initial', position: 'relative' }}
    >
      <ErrorBoundary>
        {isCompareClicked && isProviderSection && (
          <PSXHeaderCardWrapper
            backgroundColor={'$bgHeaderColor'}
            showHeaderStyleforMap={enableMapViewEnhancements}
            showSearchInputBackButton
          >
            <Breadcrumb breadcrumbs={breadcrumbs} />
          </PSXHeaderCardWrapper>
        )}
        {!mobileRouteView && !compareShareToggle && headerContent}

        {!isLoading && isBHCare && isVirtualFilterCareSelected && (
          <div
            style={{
              backgroundColor: uhcThemeOverride.theme.colors.bgHeaderColor,
            }}
          >
            <AlertBannerWrapper>
              <OutOfStateBanner providersData={results} />
            </AlertBannerWrapper>
          </div>
        )}
        {showMergedCompareAndShare && !isCompareClicked && (
          <ShowCompareFloatingSection
            compareShareToggle={compareShareToggle}
            containerRef={containerRef}
            headers={headers}
            isTieredPlan={isTieredPlan}
            items={items}
            map={map}
            mobileScreen={mobileScreen}
            openCompare={openCompare}
            recommendationPreferences={recommendationPreferences}
            removeItem={removeItem}
            results={results}
            selectedCheckbox={selectedCheckbox}
            selectedItems={selectedItems}
            setCompareShareToggle={setCompareShareToggle}
            setIsCompareClicked={setIsCompareClicked}
            setItems={setItems}
            setOpenCompare={setOpenCompare}
            setSelectedCheckbox={setSelectedCheckbox}
            setSelectedItems={setSelectedItems}
            showMergedCompareAndShare={showMergedCompareAndShare}
            urlCheck={urlCheck}
          />
        )}

        {!isVirtualCareListView && (
          <AlertsContent
            eapCodeFlag={eapCodeFlag}
            enableMapViewEnhancements={enableMapViewEnhancements}
            enableMapViewHideOption={enableMapViewHideOption}
            expandedProviderListView={expandedProviderListView}
            isBHCare={isBHCare}
            navigateToDirections={navigateToDirections}
            showAlerts={showAlerts}
          />
        )}
        <ErrorBoundary>
          <PageContainer
            css={{ position: 'relative' }}
            data-auto-testid="viewall-container"
          >
            <React.Fragment>
              {showCompareExpandedView && (
                <CompareSmartChoice
                  choosePCP={choosePCP}
                  editCompareButtonRef={editCompareButtonRef}
                  headers={headers}
                  isTieredPlan={isTieredPlan}
                  recommendationPreferences={recommendationPreferences}
                  removeItem={removeItem}
                  selectedAttributes={items}
                  selectedProviders={selectedItems}
                  setCompareClicked={setIsCompareClicked}
                  setIsEditCompareOpenSmartChoice={
                    setIsEditCompareOpenSmartChoice
                  }
                  setIsPickProviderModalOpen={setIsPickProviderModalOpen}
                  setItems={setItems}
                  setPickProviderIndex={setPickProviderIndex}
                />
              )}
              {!showCompareExpandedView && (
                <>
                  {isVirtualCareListView ? (
                    virtualCareContent
                  ) : (
                    <>
                      {!compareShareToggle && filterViewContent}
                      {mapViewContent}
                      {mobileMapViewContent}
                    </>
                  )}
                </>
              )}
              {showMergedCompareAndShare && isEditCompareOpenSmartChoice && (
                <EditCompareDrawerSmartChoice
                  editCompareButtonRef={editCompareButtonRef}
                  isEditCompareOpenSmartChoice={isEditCompareOpenSmartChoice}
                  items={items?.filter((item) =>
                    'isDisabled' in item ? !item.isDisabled : false
                  )}
                  setIsEditCompareOpenSmartChoice={
                    setIsEditCompareOpenSmartChoice
                  }
                  setItems={setItems}
                  showMergedCompareAndShare={showMergedCompareAndShare}
                />
              )}
              {showMergedCompareAndShare &&
                mobileScreen &&
                isPickProviderModalOpen && (
                  <PickProviderModal
                    isPickProviderModalOpen={isPickProviderModalOpen}
                    onSwapProviderPress={onSwapProviderPress}
                    pickProviderIndex={pickProviderIndex}
                    selectedProviders={selectedItems}
                    setIsPickProviderModalOpen={setIsPickProviderModalOpen}
                  />
                )}
            </React.Fragment>
          </PageContainer>
        </ErrorBoundary>
        <ErrorBoundary>
          <ViewAllDisclaimers
            isVisionCare={isVisionCare}
            sectionType={sectionType}
          />
          <EducateCompareModalPopup
            onPressTryItNow={() => {
              setCompareShareToggle(true);
              setOpenCompare(true);
            }}
            sectionType={sectionType}
          />
        </ErrorBoundary>
      </ErrorBoundary>
    </div>
  );
};
